import React, { useState, useEffect } from 'react';
import { AlertTriangle, BarChart3, Calendar, Download, TrendingUp, Trophy, UserCheck, Users } from 'lucide-react';
import { api } from '../api';
import { LeaderboardTable } from '../components/common/LeaderboardTable';
import { useTranslation } from 'react-i18next';

const PartnerAdminDashboard = ({ user, onNavigate }) => {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [setupStatus, setSetupStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  // Stellar Reseller state
  const [solutionAreas, setSolutionAreas] = useState([]);
  const [stellarArea, setStellarArea] = useState('all');
  const [stellarData, setStellarData] = useState([]);
  const [stellarLoading, setStellarLoading] = useState(false);
  // Reseller Insights state
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsData, statusData, areasData] = await Promise.all([
          api.request('/partner-admin/analytics'),
          api.request('/partner-admin/setup-status'),
          api.request('/analytics/solution-areas'),
        ]);
        setAnalytics(analyticsData);
        setSetupStatus(statusData);
        setSolutionAreas(areasData);
      } catch (err) {
        console.error('Failed to load partner admin data:', err);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'stellar') {
      setStellarLoading(true);
      api.request(`/analytics/stellar-reseller-leaderboard?solution_area=${stellarArea}`)
        .then(d => { setStellarData(d); setStellarLoading(false); })
        .catch(() => setStellarLoading(false));
    }
    if (activeTab === 'insights') {
      setInsightsLoading(true);
      api.request('/partner-admin/reseller-insights')
        .then(d => { setInsights(d); setInsightsLoading(false); })
        .catch(() => setInsightsLoading(false));
    }
  }, [activeTab, stellarArea]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-12 h-12 border-4 border-levelup-accent border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: t('partnerAdmin.overview'), icon: BarChart3 },
    { id: 'stellar', label: t('partnerAdmin.stellarLeaderboard'), icon: Trophy },
    { id: 'insights', label: t('partnerAdmin.resellerInsights'), icon: TrendingUp },
  ];

  return (
    <div data-testid="partner-admin-dashboard">
      <h1 className="text-2xl font-bold text-levelup-dark mb-2">{t('partnerAdmin.dashboardTitle')}</h1>
      <p className="text-gray-600 mb-6">
        {t('partnerAdmin.dashboardDesc')} <span className="font-semibold">{user.domain}</span>
      </p>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-3">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            data-testid={`pad-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-levelup-dark text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            <tab.icon size={16} />{tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {(!setupStatus?.designated_trainers || setupStatus.designated_trainers.length === 0) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5 mb-6" data-testid="trainer-reminder-banner">
              <div className="flex items-start">
                <AlertTriangle className="h-6 w-6 text-yellow-500 flex-shrink-0" />
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-yellow-800">{t('partnerAdmin.addTrainersBanner')}</h3>
                  <p className="mt-1 text-sm text-yellow-700">{t('partnerAdmin.addTrainersBannerDesc')}</p>
                  <button onClick={() => onNavigate && onNavigate('trainer-management')}
                    className="mt-4 inline-flex items-center px-4 py-2 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition-colors"
                    data-testid="add-trainers-cta">
                    <Users size={18} className="mr-2" />{t('partnerAdmin.goToTrainerMgmt')}
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-lg p-6"><div className="flex items-center justify-between mb-2"><span className="text-gray-500 text-sm">{t('partnerAdmin.totalTrainers')}</span><Users className="text-levelup-accent" size={20} /></div><p className="text-3xl font-bold text-levelup-dark">{analytics?.total_trainers || 1}</p></div>
            <div className="bg-white rounded-xl shadow-lg p-6"><div className="flex items-center justify-between mb-2"><span className="text-gray-500 text-sm">{t('partnerAdmin.downloads')}</span><Download className="text-levelup-teal" size={20} /></div><p className="text-3xl font-bold text-levelup-dark" data-testid="pad-stat-downloads">{analytics?.total_downloads || 0}</p></div>
            <div className="bg-white rounded-xl shadow-lg p-6"><div className="flex items-center justify-between mb-2"><span className="text-gray-500 text-sm">{t('partnerAdmin.workshops')}</span><Calendar className="text-blue-500" size={20} /></div><p className="text-3xl font-bold text-levelup-dark">{analytics?.total_workshops || 0}</p></div>
            <div className="bg-white rounded-xl shadow-lg p-6"><div className="flex items-center justify-between mb-2"><span className="text-gray-500 text-sm">{t('partnerAdmin.learnersTrained')}</span><UserCheck className="text-green-500" size={20} /></div><p className="text-3xl font-bold text-levelup-dark">{analytics?.total_learners_trained || 0}</p></div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-levelup-dark">{t('partnerAdmin.designatedTrainers')}</h2>
              <span className="text-sm text-gray-500">{(setupStatus?.designated_trainers?.length || 0) + 1} / {setupStatus?.max_trainers || 5} {t('trainerManagement.trainersCount')}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-levelup-light rounded-lg mb-3">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-levelup-accent flex items-center justify-center text-levelup-darker font-bold mr-3">{setupStatus?.admin_full_name?.charAt(0) || 'A'}</div>
                <div><p className="font-medium text-levelup-dark">{setupStatus?.admin_email}</p><p className="text-sm text-gray-500">{setupStatus?.admin_full_name} (You)</p></div>
              </div>
              <span className="px-3 py-1 bg-levelup-accent text-levelup-darker rounded-full text-xs font-medium">{t('partnerAdmin.partnerAdminBadge')}</span>
            </div>
            {setupStatus?.designated_trainers?.map((trainer) => (
              <div key={trainer.id} className="flex items-center justify-between p-4 border rounded-lg mb-3">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-bold mr-3">{trainer.full_name?.charAt(0) || trainer.email.charAt(0).toUpperCase()}</div>
                  <div><p className="font-medium text-levelup-dark">{trainer.email}</p><p className="text-sm text-gray-500">{trainer.full_name || 'No name provided'}</p></div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${trainer.is_approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{trainer.is_approved ? 'Active' : 'Pending'}</span>
              </div>
            ))}
            {(!setupStatus?.designated_trainers || setupStatus.designated_trainers.length === 0) && (
              <p className="text-gray-500 text-center py-4">No additional trainers designated yet.</p>
            )}
          </div>
        </>
      )}

      {/* Stellar Reseller Leaderboard Tab */}
      {activeTab === 'stellar' && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center gap-3 mb-2">
            <Trophy size={24} className="text-levelup-accent" />
            <h2 className="text-xl font-bold text-levelup-dark">Stellar Reseller Leaderboard</h2>
          </div>
          <p className="text-sm text-gray-500 mb-6">Your top resellers ranked by training attendance across your delivered workshops</p>
          <LeaderboardTable
            data={stellarData}
            domainKey="reseller_domain"
            loading={stellarLoading}
            solutionAreas={solutionAreas}
            selectedArea={stellarArea}
            onAreaChange={(area) => setStellarArea(area)}
          />
        </div>
      )}

      {/* Reseller Insights Tab */}
      {activeTab === 'insights' && (
        <div className="space-y-6">
          {insightsLoading ? (
            <div className="flex justify-center py-10"><div className="animate-spin w-8 h-8 border-4 border-levelup-accent border-t-transparent rounded-full" /></div>
          ) : insights ? (
            <>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-levelup-teal">
                  <p className="text-gray-500 text-sm">Total Unique Resellers</p>
                  <p className="text-3xl font-bold text-levelup-dark mt-1">{insights.total_resellers}</p>
                  <p className="text-xs text-gray-400 mt-1">Across all completed workshops</p>
                </div>
                <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
                  <p className="text-gray-500 text-sm">Returning Resellers</p>
                  <p className="text-3xl font-bold text-levelup-dark mt-1">{insights.returning_resellers}</p>
                  <p className="text-xs text-gray-400 mt-1">{insights.return_rate_pct}% return rate</p>
                </div>
                <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
                  <p className="text-gray-500 text-sm">Avg Attendees / Workshop</p>
                  <p className="text-3xl font-bold text-levelup-dark mt-1">{insights.avg_attendees_per_workshop}</p>
                  <p className="text-xs text-gray-400 mt-1">Workshop effectiveness</p>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-semibold text-levelup-dark mb-4 flex items-center gap-2">
                  <TrendingUp size={20} className="text-levelup-accent" /> Monthly Training Trend
                </h3>
                <div className="flex items-start gap-6 mb-2 text-xs text-gray-500 font-medium">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-levelup-dark rounded-sm inline-block" /> Workshops</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-levelup-accent rounded-sm inline-block" /> Learners (÷10)</span>
                </div>
                {insights.monthly_trend.length === 0 ? (
                  <p className="text-gray-400 text-sm">No monthly data yet</p>
                ) : (
                  <div className="space-y-3">
                    {insights.monthly_trend.map((m, i) => (
                      <div key={i} className="flex items-center gap-4">
                        <span className="text-sm text-gray-500 w-16 flex-shrink-0">{m.month}</span>
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <div className="bg-levelup-dark h-5 rounded flex items-center px-2 text-white text-xs font-medium" style={{ width: `${Math.max(m.workshops * 20, 24)}px` }}>{m.workshops}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="bg-levelup-accent h-5 rounded flex items-center px-2 text-levelup-darker text-xs font-medium" style={{ width: `${Math.max(Math.round(m.learners / 5), 24)}px` }}>{m.learners}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-semibold text-levelup-dark mb-1">Last 30 Days vs Previous 30 Days</h3>
                <p className="text-sm text-gray-500 mb-4">How your delivery pace is trending</p>
                <div className="flex gap-6">
                  <div className="flex-1 p-4 bg-levelup-light rounded-lg text-center">
                    <p className="text-3xl font-bold text-levelup-dark">{insights.workshops_last_30d}</p>
                    <p className="text-sm text-gray-500">Last 30 days</p>
                  </div>
                  <div className="flex-1 p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-gray-400">{insights.workshops_prev_30d}</p>
                    <p className="text-sm text-gray-500">Previous 30 days</p>
                  </div>
                  <div className="flex-1 p-4 rounded-lg text-center flex flex-col items-center justify-center">
                    {insights.workshops_last_30d > insights.workshops_prev_30d ? (
                      <><TrendingUp size={32} className="text-green-500" /><p className="text-sm font-semibold text-green-600 mt-1">Trending Up</p></>
                    ) : insights.workshops_last_30d < insights.workshops_prev_30d ? (
                      <><TrendingUp size={32} className="text-red-400 rotate-180" /><p className="text-sm font-semibold text-red-500 mt-1">Slower pace</p></>
                    ) : (
                      <><span className="text-2xl">→</span><p className="text-sm font-semibold text-gray-500 mt-1">Steady</p></>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : <p className="text-gray-500 text-center py-10">No data available yet.</p>}
        </div>
      )}
    </div>
  );
};

// Trainer Management Component for Partner Admin

export default PartnerAdminDashboard;
